import * as React from 'react';
import { Select, Tooltip } from 'antd';
import { useAtomValue } from 'jotai';
import debounce from 'lodash/debounce';
import { errorDataAtom } from '@src/components/metrics-panel-hz/atoms/index';
import { getMetricByUnit, getMetricMetaByKey } from '@src/utils/metrics/metricHelpers';
import { useDimensionOperations } from '../../hooks/useDimensionOperations';
import * as utils from './utils';
import { MapContainer } from './styled';

type DimensionMapDataItem = Record<string, number> & {
  name: [string];
  value: [string];
  coordinate?: google.maps.LatLngLiteral;
};

export interface DimensionMapProps {
  defaultMetric: string;
  dimension: string;
  unit: string;
  data: DimensionMapDataItem[];
}

export interface DimensionMapRef {
  resetMapCenter: () => void;
}

const DimensionMap = React.forwardRef<DimensionMapRef, DimensionMapProps>((props, ref) => {
  const { data, dimension, defaultMetric, unit } = props;
  const errorData = useAtomValue(errorDataAtom);
  const { dimensionMetrics: metrics } = useDimensionOperations();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<google.maps.Map>();
  const infoWindowRef = React.useRef<google.maps.InfoWindow>();
  const initialMapCenterRef = React.useRef(false);
  const visibleMarkerMapRef = React.useRef(new Map<string, google.maps.Marker>());
  const [metric, setMetric] = React.useState(defaultMetric);
  const [bounds, setBounds] = React.useState<google.maps.LatLngBounds>();
  const metricOptions = React.useMemo(
    () =>
      metrics
        .filter((x) => !errorData[x])
        .map((it) => {
          const meta = getMetricMetaByKey({ metricKey: it });
          const res: { name: string; displayName: string; short: string } = {
            name: meta.name,
            displayName: meta.displayName,
            short: meta.short,
          };

          if (unit === '%' || unit === '%T') {
            const percentageMeta = getMetricMetaByKey({ metricKey: getMetricByUnit(unit, it) });
            res.short = percentageMeta.short;
            res.displayName = percentageMeta.displayName;
          }

          return res;
        }),
    [metrics, unit, errorData],
  );
  const currentMetricMeta = React.useMemo(
    () => getMetricMetaByKey({ metricKey: getMetricByUnit(unit, metric) }),
    [metric, unit],
  );
  const dataWithCoordinate = React.useMemo(
    () => data.filter((it) => !!it.coordinate) as Required<DimensionMapDataItem>[],
    [data],
  );

  // Inital google map, add bounds_changed listener
  React.useEffect(() => {
    if (!containerRef.current) return;

    mapRef.current = new google.maps.Map(containerRef.current, utils.mapInitOptions);
    infoWindowRef.current = new google.maps.InfoWindow();

    const debouncedSetBounds = debounce(setBounds, 500);
    mapRef.current.addListener('bounds_changed', () => {
      debouncedSetBounds(mapRef.current!.getBounds());
    });

    const markerMap = visibleMarkerMapRef.current;

    return () => {
      if (mapRef.current) {
        google.maps.event.clearListeners(mapRef.current, 'bounds_changed');
        mapRef.current.unbindAll();
      }

      if (infoWindowRef.current) {
        infoWindowRef.current.unbindAll();
        infoWindowRef.current.close();
      }

      Array.from(markerMap.values()).forEach((it) => {
        google.maps.event.clearListeners(it, 'mouseover');
        google.maps.event.clearListeners(it, 'mouseout');
        it.unbindAll();
        it.setMap(null);
      });
    };
  }, []);

  // When the metric change, should reset the internal metric state
  React.useEffect(() => {
    if (metrics.find((it) => it === defaultMetric)) {
      setMetric(defaultMetric);
    } else {
      setMetric(metrics[0]);
    }

    // Only response the defaultMetric change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMetric]);

  // Reset the internal metric state when the metric metadata change
  React.useEffect(() => {
    setMetric((v) => metrics.find((it) => it === v) || metrics[0]);
  }, [metrics]);

  // Reset the map center and zoom symbol
  React.useEffect(() => {
    initialMapCenterRef.current = false;
  }, [metric]);

  // Set the map center and zoom
  React.useEffect(() => {
    if (!mapRef.current || initialMapCenterRef.current) return;

    const center = utils.getFirstPointToMapCenter(dataWithCoordinate);

    if (center) {
      mapRef.current.setCenter(center);
      mapRef.current.setZoom(utils.getMapZoom(dimension));

      initialMapCenterRef.current = true;
    }
  }, [dataWithCoordinate, dimension, metric]);

  React.useEffect(() => {
    if (!currentMetricMeta || !mapRef.current || !infoWindowRef.current) return;

    const currentBounds = bounds || mapRef.current.getBounds();
    // Filter out the data which is not in the current map range
    const visibleData = dataWithCoordinate.filter((it) =>
      currentBounds?.contains(new google.maps.LatLng(it.coordinate.lat, it.coordinate.lng)),
    );

    // Get the diff between the current visible points and the previous,
    // and then apply to the markers
    const { removed } = utils.diffVisiblePoints(
      Array.from(visibleMarkerMapRef.current.keys()),
      visibleData.map((it) => it.value[0]),
    );

    const markerMap = visibleMarkerMapRef.current;

    // Remove the markers that are not in the current map range
    removed.forEach((it) => {
      const marker = markerMap.get(it);

      if (!marker) return;

      utils.deleteMarker(marker);
      markerMap.delete(it);
    });

    visibleData.forEach((it, ix, arr) => {
      const geoCode = it.value[0];

      // Need to update the marker
      if (markerMap.has(geoCode)) {
        utils.updateMarker(markerMap.get(geoCode)!, it, currentMetricMeta, arr.length - ix);

        return;
      }

      // New markers
      const marker = utils.createMarker(
        it,
        currentMetricMeta,
        arr.length - ix,
        infoWindowRef.current!,
        mapRef.current!,
      );

      markerMap.set(it.value[0], marker);
    });
  }, [dataWithCoordinate, dimension, currentMetricMeta, bounds]);

  React.useImperativeHandle(
    ref,
    () => ({
      resetMapCenter: () => {
        initialMapCenterRef.current = false;
      },
    }),
    [],
  );

  return (
    <MapContainer>
      <div ref={containerRef} className="map-content" />
      {metricOptions.length > 1 && (
        <Select
          showSearch
          value={metric}
          options={metricOptions.map((it) => ({
            value: it.name,
            label: (
              <Tooltip title={it.displayName} placement="left">
                {it.short}
              </Tooltip>
            ),
            contentLabel: it.short,
          }))}
          optionLabelProp="contentLabel"
          optionFilterProp="label"
          className="map-metric-select"
          onChange={setMetric}
        />
      )}
    </MapContainer>
  );
});

DimensionMap.displayName = 'DimensionMap';

export default React.memo(DimensionMap);
